import { DialogContent } from '@/components/ui/dialog';
import { ModalLayout, ModalTitle } from '@/components/ui/modal-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem } from '@/components/ui/select';
import { Field, FieldLabel, FieldContent } from '@/components/ui/field';
import { Form } from '../../Form';
import { useFormContext } from '../../Form/FormProvider';
import { useTranslation } from '@hooks/useTranslation';
import { createInventoryFormSchema, type CreateInventoryFormData } from '../../../schemas/inventory.schema';
import { cn } from '@/lib/utils';
import { DropzoneUpload } from '../../common/ImageUpload';

interface CreateNonDefaultFormProps {
  categories: string[];
  locations: string[];
  isPending: boolean;
  images: File[];
  onImagesChange: (files: File[]) => void;
  onSubmit: (values: CreateInventoryFormData) => Promise<void>;
  onClose: () => void;
}

interface FormFieldsProps {
  categories: string[];
  locations: string[];
  images: File[];
  onImagesChange: (files: File[]) => void;
}

function FormFields({ categories, locations, images, onImagesChange }: FormFieldsProps) {
  const { form } = useFormContext();
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6">
      {/* Name */}
      <form.Field name="name">
        {(field) => (
          <Field>
            <FieldLabel className="font-inter text-sm font-semibold text-grey">
              {t('common.overview.name')}
            </FieldLabel>
            <FieldContent>
              <Input
                value={field.state.value as string}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t('create_modal.create_form.placeholders.name')}
                variant="form"
              />

            </FieldContent>
          </Field>
        )}
      </form.Field>

      {/* Category + Location */}
      <div className="grid grid-cols-2 gap-6">
        <form.Field name="category">
          {(field) => (
            <Field>
              <FieldLabel className="font-inter text-sm font-semibold text-grey">
                {t('common.overview.category')}
              </FieldLabel>
              <FieldContent>
                <Select
                  value={(field.state.value as string) ?? ''}
                  onValueChange={field.handleChange}
                >
                  <SelectTrigger>
                    <span className={cn(field.state.value ? 'text-off-black' : 'text-grey-light')}>
                      {(field.state.value as string) || t('create_modal.create_form.placeholders.category')}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
  
              </FieldContent>
            </Field>
          )}
        </form.Field>

        <form.Field name="location">
          {(field) => (
            <Field>
              <FieldLabel className="font-inter text-sm font-semibold text-grey">
                {t('common.overview.location')}
              </FieldLabel>
              <FieldContent>
                <Select
                  value={(field.state.value as string) ?? ''}
                  onValueChange={field.handleChange}
                >
                  <SelectTrigger>
                    <span className={cn(field.state.value ? 'text-off-black' : 'text-grey-light')}>
                      {(field.state.value as string) || t('create_modal.create_form.placeholders.location')}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc} value={loc}>
                        {loc}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
  
              </FieldContent>
            </Field>
          )}
        </form.Field>
      </div>

      {/* Retail price + Cost price */}
      <div className="grid grid-cols-2 gap-6">
        <form.Field name="recommended_retail_price">
          {(field) => (
            <Field>
              <div className="flex items-center justify-between">
                <FieldLabel className="font-inter text-sm font-semibold text-grey">
                  {t('common.overview.retail_price')}
                </FieldLabel>
                <span className="font-inter text-xs text-grey">
                  {t('edit.optional')}
                </span>
              </div>
              <FieldContent>
                <div className="relative">
                  <span className="absolute left-6 top-1/2 -translate-y-1/2 font-inter text-sm text-grey">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={field.state.value as number | string ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.handleChange(val === '' ? null : parseFloat(val));
                    }}
                    onBlur={field.handleBlur}
                    variant="form"
                    className="pl-10"
                  />
                </div>

              </FieldContent>
            </Field>
          )}
        </form.Field>

        <form.Field name="cost_to_company">
          {(field) => (
            <Field>
              <div className="flex items-center justify-between">
                <FieldLabel className="font-inter text-sm font-semibold text-grey">
                  {t('common.overview.cost_price')}
                </FieldLabel>
                <span className="font-inter text-xs text-grey">
                  {t('edit.optional')}
                </span>
              </div>
              <FieldContent>
                <div className="relative">
                  <span className="absolute left-6 top-1/2 -translate-y-1/2 font-inter text-sm text-grey">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={field.state.value as number | string ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.handleChange(val === '' ? null : parseFloat(val));
                    }}
                    onBlur={field.handleBlur}
                    variant="form"
                    className="pl-10"
                  />
                </div>

              </FieldContent>
            </Field>
          )}
        </form.Field>
      </div>

      {/* Upload images */}
      <Field>
        <FieldLabel className="font-inter text-sm font-semibold text-grey">
          {t('create_modal.create_form.upload_images')}
        </FieldLabel>
        <DropzoneUpload files={images} onFilesChange={onImagesChange} />
      </Field>
    </div>
  );
}

function FormFooter({ isPending }: { isPending: boolean }) {
  const { form } = useFormContext();
  const { t } = useTranslation();

  return (
    <form.Subscribe
      selector={(state) => ({
        isSubmitting: state.isSubmitting,
      })}
    >
      {({ isSubmitting }) => {
        const isLoading = isSubmitting || isPending;

        return (
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              type="submit"
              disabled={isLoading}
            >
              {t('create_modal.create_form.create')}
            </Button>
          </div>
        );
      }}
    </form.Subscribe>
  );
}

export function CreateNonDefaultForm({
  categories,
  locations,
  isPending,
  images,
  onImagesChange,
  onSubmit,
  onClose,
}: CreateNonDefaultFormProps) {
  const { t } = useTranslation();

  return (
    <DialogContent
      onOpenAutoFocus={(e) => e.preventDefault()}
      onEscapeKeyDown={onClose}
      onInteractOutside={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('[data-radix-popper-content-wrapper]')) {
          e.preventDefault();
        }
      }}
      className="max-w-dialog max-h-[90vh] rounded-input border-dinnerplate-white bg-body p-0 outline-none shadow-none [&>button:last-child]:hidden"
    >
      <Form
        schema={createInventoryFormSchema}
        defaultValues={{
          name: '',
          category: '',
          location: '',
          recommended_retail_price: null,
          cost_to_company: null,
        }}
        onSubmit={onSubmit}
        isPending={isPending}
        className="flex flex-1 flex-col min-h-0"
      >
        <ModalLayout
          as="div"
          title={<ModalTitle>{t('create_modal.create_form.title')}</ModalTitle>}
          onClose={onClose}
          footer={<FormFooter isPending={isPending} />}
        >
          <FormFields categories={categories} locations={locations} images={images} onImagesChange={onImagesChange} />
        </ModalLayout>
      </Form>
    </DialogContent>
  );
}
